import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BellRing, Check, CheckCheck, MessageCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/billing";
import { ago } from "@/pages/team/DeviceStatus";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CsvChip, CsvPicker } from "@/pages/team/CsvAttach";

const picks = (m) => {
  const d = m.devices || {};
  return [
    "Batch ready for you",
    !d.phone?.installed && !d.pc?.installed && "Please install the SnapList app",
    !(d.phone?.alerts || d.pc?.alerts) && "Please turn on alerts so you see new work",
    "Can you work today?",
    "Great work today, thank you!",
  ].filter(Boolean);
};

function Status({ n }) {
  if (n.reply) return null;
  return n.read_at
    ? <span className="inline-flex items-center gap-0.5 text-[10px] text-[#0053DA]" data-testid={`nudge-read-${n.id}`}><CheckCheck className="h-3 w-3" /> Read</span>
    : <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400" data-testid={`nudge-sent-${n.id}`}><Check className="h-3 w-3" /> Sent</span>;
}

function Thread({ items }) {
  const end = useRef(null);
  useEffect(() => { if (end.current) end.current.scrollTop = end.current.scrollHeight; }, [items.length]);
  if (!items.length) return <p className="text-xs text-slate-400" data-testid="nudge-thread-empty">No messages yet.</p>;
  return (
    <ul ref={end} className="max-h-56 space-y-2 overflow-y-auto pr-1" data-testid="nudge-thread">
      {[...items].reverse().map((n) => (
        <li key={n.id} className="space-y-1">
          {n.from_assistant ? <>
            <div className="w-fit max-w-[85%] rounded-2xl rounded-bl-sm bg-amber-50 px-3 py-1.5 text-sm text-slate-800 ring-1 ring-amber-200" data-testid={`nudge-from-asst-${n.id}`}>
              {n.batch_name && <span className="block text-[10px] font-semibold text-amber-700">{n.batch_name}</span>}{n.text}
              <CsvChip csv={n.csv} />
            </div>
            <p className="text-[10px] text-slate-400">{ago(n.created_at)?.replace("active ", "")}</p>
          </> : <>
          <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-[#0053DA] px-3 py-1.5 text-sm text-white">{n.text}<CsvChip csv={n.csv} dark /></div>
          <p className="flex justify-end gap-1.5 text-[10px] text-slate-400">{ago(n.created_at)?.replace("active ", "")} <Status n={n} /></p>
          {n.reply && <div className="w-fit max-w-[85%] rounded-2xl rounded-bl-sm bg-slate-100 px-3 py-1.5 text-sm text-slate-800" data-testid={`nudge-reply-${n.id}`}>{n.reply}<CsvChip csv={n.reply_csv} /></div>}
          </>}
        </li>
      ))}
    </ul>
  );
}

// Owner -> assistant nudge: free phone alert + their in-app inbox; their replies show in the thread.
export default function NudgeButton({ m }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [csv, setCsv] = useState(null);
  const load = () => api.get(`/team/nudges`, { params: { member_id: m.id } }).then(({ data }) => setItems(data.nudges)).catch(() => {});
  useEffect(() => { load(); }, [m.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open) return undefined; load(); const t = setInterval(() => { if (!document.hidden) load(); }, 30000); return () => clearInterval(t); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const seenKey = `snaplist.nudgeSeen.${m.id}`;
  const [seen, setSeen] = useState(() => localStorage.getItem(seenKey) || "");
  useEffect(() => { if (open) { const t = new Date().toISOString(); localStorage.setItem(seenKey, t); setSeen(t); } }, [open, seenKey]);
  const unreadReplies = items.filter((n) => (n.reply && n.replied_at > seen) || (n.from_assistant && n.created_at > seen)).length;

  const send = async (msg) => {
    const t = (msg || text).trim() || (csv ? `Sent you a spreadsheet: ${csv.name}` : "");
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post("/team/nudges", { member_id: m.id, text: t.slice(0, 300), csv_id: csv?.id });
      setItems((x) => [data, ...x]);
      setText(""); setCsv(null);
      toast.success(data.phones ? "Sent to their phone and inbox" : "Sent to their inbox (alerts aren't on yet, so no phone pop-up)");
    } catch (e) { toast.error(errorMessage(e, "Couldn't send")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`team-member-nudge-${m.id}`}
        className="inline-flex h-8 items-center gap-1.5 rounded-full border border-[#0053DA]/40 bg-blue-50 px-3 text-xs font-semibold text-[#0053DA] transition-colors hover:bg-blue-100">
        <MessageCircle className="h-3.5 w-3.5" /> Nudge{unreadReplies ? ` · ${unreadReplies} new message${unreadReplies === 1 ? "" : "s"}` : ""}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] max-w-md rounded-xl" data-testid="nudge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BellRing className="h-4 w-4 text-[#0053DA]" /> Nudge {m.email}</DialogTitle>
            <DialogDescription>Arrives as a free phone alert and in their SnapList inbox. They can reply.</DialogDescription>
          </DialogHeader>
          <Thread items={items} />
          <div className="flex flex-wrap gap-1.5" data-testid="nudge-picks">
            {picks(m).map((p, i) => (
              <button key={p} type="button" onClick={() => send(p)} disabled={busy} data-testid={`nudge-pick-${i}`}
                className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-700 transition-colors hover:border-[#0053DA] hover:text-[#0053DA] disabled:opacity-50">{p}</button>
            ))}
          </div>
          <CsvPicker value={csv} onChange={setCsv} owner />
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
            <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 300))} rows={2} placeholder="Type a message…" data-testid="nudge-input"
              className="min-h-[44px] flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#0053DA] focus:outline-none" />
            <button type="submit" disabled={busy || (!text.trim() && !csv)} data-testid="nudge-send" aria-label="Send nudge"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0053DA] text-white transition-transform active:scale-95 disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
